import { useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";

/**
 * ReviewDialog — lets a student rate + review a completed booking.
 * Rendered on the Dashboard for bookings with status="completed" and has_review=false.
 */
export function ReviewDialog({ booking, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    // Submit is disabled while rating=0, so this is a defense-in-depth guard only.
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        booking_id: booking.booking_id,
        rating,
        comment: comment.trim(),
      });
      toast.success(`Thanks for reviewing ${booking.handyman_name}!`);
      setOpen(false);
      onReviewed?.(booking.booking_id);
    } catch (e) {
      Sentry.captureException(e);
      const msg = e.response?.data?.detail || "Could not submit review";
      toast.error(typeof msg === "string" ? msg : "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/10"
          data-testid={`review-open-${booking.booking_id}`}
        >
          <Star className="w-4 h-4 mr-1" /> Leave a review
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`review-dialog-${booking.booking_id}`}>
        <DialogHeader>
          <DialogTitle>Review {booking.handyman_name}</DialogTitle>
          <DialogDescription>
            How was the {booking.category?.replace("_", " ")} job? Your review helps other students pick the right pro.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-3" data-testid={`review-stars-${booking.booking_id}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              data-testid={`review-star-${n}`}
              className="p-1 transition-transform hover:scale-110"
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
              <Star
                className={`w-8 h-8 ${
                  n <= (hover || rating)
                    ? "fill-cognac text-cognac"
                    : "text-charcoal/30"
                }`}
              />
            </button>
          ))}
          <span className="ml-2 text-sm text-charcoal/60">
            {rating ? `${rating} / 5` : "Tap a star"}
          </span>
        </div>

        <Textarea
          placeholder="Tell other students how it went — was the pro on time, careful, worth it?"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          maxLength={800}
          data-testid={`review-comment-${booking.booking_id}`}
        />
        <p className="text-xs text-charcoal/40 text-right">{comment.length} / 800</p>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            data-testid={`review-cancel-${booking.booking_id}`}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !rating}
            data-testid={`review-submit-${booking.booking_id}`}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
          >
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
